/**
 * Doctor browsing and search.
 *
 * Replaces `renderPatientDoctors`, `filterPatientDoctors`,
 * `openPatientCategoryDoctors` and the near-identical `renderDoctors` in app.js.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  PageHeader,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useData } from '../../context/DataContext';
import { useDebounced } from '../../hooks/useLocalStorage';
import { categoryName } from '../../domain/catalog';
import { doctorPriceRange, formatPriceRange, BOOKING_TYPES } from '../../domain/pricing';
import { CategoryGrid } from './CategoryGrid';

export function PatientDoctors() {
  const { categoryId } = useParams();
  const { doctors, doctorsState, categories, loadDoctors } = useData();

  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || '');
  const debouncedQuery = useDebounced(query, 250);

  const activeCategory = categoryId || selectedCategory;

  const filtered = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase();
    return doctors.filter((doctor) => {
      if (activeCategory && doctor.category !== activeCategory) return false;
      if (!needle) return true;
      return (
        String(doctor.name).toLowerCase().includes(needle) ||
        String(doctor.title).toLowerCase().includes(needle) ||
        String(doctor.categoryName).toLowerCase().includes(needle)
      );
    });
  }, [doctors, activeCategory, debouncedQuery]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={activeCategory ? categoryName(activeCategory, categories) : 'الأطباء'}
        subtitle={`${filtered.length} طبيب متاح`}
        icon="stethoscope"
      />

      <Panel>
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ابحث باسم الطبيب أو التخصص..."
              className="pr-9"
            />
          </div>

          {!categoryId && (
            <Select
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
              className="sm:w-56"
            >
              <option value="">كل التخصصات</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          )}
        </div>
      </Panel>

      {/* The specialty grid, as the legacy "اختر التخصص" panel showed it. It sits
          above the results so the grid is how you pick, and the dropdown is the
          shortcut rather than the only way in. */}
      {!categoryId && categories.length > 0 && (
        <Panel>
          <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
            <Icon name="layout-grid" size={18} className="text-cyan-400" />
            اختر التخصص
          </h3>
          <CategoryGrid categories={categories} activeId={selectedCategory} />
        </Panel>
      )}

      {doctorsState.error ? (
        <ErrorState error={doctorsState.error} onRetry={loadDoctors} />
      ) : doctorsState.loading && doctors.length === 0 ? (
        <Spinner label="جاري تحميل الأطباء..." />
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState
            icon="user-x"
            title="لا يوجد أطباء مطابقون"
            message="جرّب تغيير التخصص أو مسح كلمة البحث."
          />
        </Panel>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {/* grid-cols-1, not a bare grid: an implicit track sizes to its widest
              card, which on a phone pushed the whole page sideways. */}
          {filtered.map((doctor) => (
            <DoctorCard key={doctor.id} doctor={doctor} categories={categories} />
          ))}
        </div>
      )}
    </div>
  );
}

function DoctorCard({ doctor, categories }) {
  const clinicCount = doctor.clinics?.length || 0;
  const hasDiscount = doctor.hasNightDiscount || doctor.hasFirstVisitDiscount;

  // The clinic's price is what the patient is actually charged, so it is what
  // the card has to show. Reading `doctor.price_exam` here quoted the
  // DoctorProfile column default — a patient saw one number on this card and a
  // different one on the booking page.
  const examRange = doctorPriceRange({ doctor, bookingType: BOOKING_TYPES.EXAM });
  const followupRange = doctorPriceRange({ doctor, bookingType: BOOKING_TYPES.FOLLOWUP });

  return (
    <Panel className="hover-card flex flex-col gap-4" padded={false}>
      <div className="flex items-start gap-3 p-5 pb-0">
        <Avatar src={doctor.image} name={doctor.name} size={56} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-bold text-white">{doctor.name}</h3>
          <p className="truncate text-xs text-slate-400">
            {doctor.title || categoryName(doctor.category, categories)}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">
              {categoryName(doctor.category, categories)}
            </Badge>
            {hasDiscount && (
              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="percent">
                خصومات
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 px-5 text-center">
        <div className="rounded-xl bg-white/5 p-2.5">
          <p className="text-[10px] text-slate-400">الكشف</p>
          <p className="text-sm font-bold text-white">{formatPriceRange(examRange)}</p>
        </div>
        <div className="rounded-xl bg-white/5 p-2.5">
          <p className="text-[10px] text-slate-400">المتابعة</p>
          <p className="text-sm font-bold text-white">{formatPriceRange(followupRange)}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-white/5 p-4">
        <span className="flex items-center gap-1 text-[11px] text-slate-400">
          <Icon name="building-2" size={13} />
          {clinicCount > 0 ? `${clinicCount} عيادة` : 'لا توجد عيادات'}
        </span>
        <Link
          to={`/patient/book/${doctor.id}`}
          className={clinicCount > 0 ? 'btn-primary' : 'btn-ghost pointer-events-none opacity-50'}
        >
          <Icon name="calendar-plus" size={16} />
          احجز الآن
        </Link>
      </div>
    </Panel>
  );
}

export default PatientDoctors;
